import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import BoxWrapper from 'blocks/BoxWrapper';
import Card from 'blocks/Card';
import EmptyBox from 'components/EmptyBox';
import Loading from 'components/Loading';
import Pager from 'components/Pager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExpandableRowProps, TablePropsType } from './table.interface';
import * as S from './table.styles';

const ExpandableRow = ({ rowItem, tableName }: ExpandableRowProps) => {
  const [expand, setExpand] = useState(true);
  let extraRow: any = [];

  for (let i = 0; i < rowItem.children[0].length - 1; i++) {
    extraRow.push(<S.TableCell $toghtder={true} key={`expand-item-${i}`} />);
  }

  return (
    <>
      <S.TableRow onClick={() => setExpand(!expand)}>
        <S.TableCell $toghtder={true}>
          <S.TitleExpand>
            <S.ExpandableRowSpan>{rowItem.title}</S.ExpandableRowSpan>
            <S.ExpandableRowSpan>
              <S.ExpandableRowSvg $active={expand} as={ArrowDropDownIcon} />
            </S.ExpandableRowSpan>
          </S.TitleExpand>
        </S.TableCell>
        {extraRow}
      </S.TableRow>
      {rowItem.children.map((childItem, index) => (
        <S.TableRow $expandable={true} $expand={expand} key={`${tableName}-expandable-${index}`}>
          {childItem.map((item, i) => (
            <S.TableRowCell className={` ${item.className ? item.className : ''}`} key={`${tableName}-row-item-${i}`}>
              {item.content}
            </S.TableRowCell>
          ))}
        </S.TableRow>
      ))}
    </>
  );
};

const Table: React.FC<TablePropsType> = ({
  head,
  rows,
  name,
  page,
  total,
  limit,
  next,
  prev,
  boxed,
  loading,
  type,
}) => {
  const { t, i18n } = useTranslation();
  const isLTR = i18n.dir() === 'ltr';
  const renderTableHead = (): JSX.Element => (
    <S.TableHeadRow>
      {head.map(
        (headItem, index): JSX.Element => (
          <S.TableHeadCell
            $dir={isLTR}
            className={` ${headItem.className ? headItem.className : ''}`}
            key={`${name}-head-${index}`}
          >
            {headItem.text}
          </S.TableHeadCell>
        )
      )}
    </S.TableHeadRow>
  );

  const renderTableRows = () => (type === 'expandable' ? renderExpandableTableRow() : renderNormalTableRow());

  const renderNormalTableRow = () =>
    rows.map((rowItem, index) => (
      <S.TableRow key={`${name}-row-${index}`}>
        {rowItem.map((item, i) => (
          <S.TableRowCell className={` ${item.className ? item.className : ''}`} key={`${name}-row-item-${i}`}>
            {item.content}
          </S.TableRowCell>
        ))}
      </S.TableRow>
    ));

  const renderExpandableTableRow = () =>
    rows.map((rowItem, index) => <ExpandableRow key={`${name}-row-${index}`} rowItem={rowItem} tableName={name} />);

  const renderTable = () => (
    <S.Table>
      <S.TableHead>{renderTableHead()}</S.TableHead>
      <S.TableBody>{renderTableRows()}</S.TableBody>
    </S.Table>
  );

  if (loading) return <Loading />;

  if (!rows.length) return <EmptyBox title={t('table.emptyText')} boxed={boxed} />;

  return (
    <>
      <BoxWrapper>
        {boxed ? (
          <Card>
            <div style={{ overflow: 'auto' }}>{renderTable()}</div>{' '}
          </Card>
        ) : (
          <div style={{ overflow: 'auto' }}>{renderTable()}</div>
        )}
      </BoxWrapper>

      {!!next && !!prev && (
        <BoxWrapper>
          <Pager next={next} prev={prev} total={total || 0} page={page || 0} limit={limit || 10} />
        </BoxWrapper>
      )}
    </>
  );
};

export default Table;
